import { useState, useEffect } from "react";
import "@/App.css";
import { translations, languageNames, getBookingUrl, detectLanguage, getChatbotUrl } from "./translations";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Sheet, SheetContent, SheetTrigger } from "./components/ui/sheet";
import { Separator } from "./components/ui/separator";
import { Switch } from "./components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "./components/ui/carousel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Menu,
  X,
  Globe,
  Phone,
  Mail,
  MapPin,
  Dog,
  Utensils,
  Wine,
  Snowflake,
  TrainFront,
  Bus,
  Check,
  ChevronDown,
  Bed,
  Star,
  Cookie,
  Shield,
  Bot
} from "lucide-react";

// Image assets
const images = {
  hero: "/images/hotel-hero.jpg",
  landscape: "/images/ski-samedan.webp",
  rooms: {
    standard: "/images/camera-standard.jpeg",
    superior: "/images/camera-superior.jpeg",
    mountainView: "/images/camera-vista.jpeg",
    balcony: "/images/camera-balcone.jpeg",
    family: [
      "/images/camera-famiglia-1.jpeg",
      "/images/camera-famiglia-2.jpeg",
      "/images/camera-famiglia-3.jpeg"
    ]
  },
  dining: {
    restaurant: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80",
    breakfast: "https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=800&q=80"
  },
  golf: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&q=80"
};

// Social Icons (SVG components for better quality)
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Obfuscated email to prevent bot scraping
const ObfuscatedEmail = () => {
  const user = "desalpes";
  const domain = "sbhsgroup.ch";
  const handleClick = () => {
    window.location.href = `mailto:${user}@${domain}`;
  };
  return (
    <button 
      onClick={handleClick}
      className="text-snow-cream/90 hover:text-muted-gold transition-colors underline-offset-2 hover:underline"
      data-testid="email-link"
    >
      {user}[at]{domain.replace('.', '[dot]')}
    </button>
  );
};

// Navigation Component
const Navigation = ({ t, lang, setLang, scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: "home", href: "#home" },
    { key: "rooms", href: "#rooms" },
    { key: "services", href: "#services" },
    { key: "location", href: "#location" },
    { key: "contact", href: "#contact" }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-white/95 backdrop-blur-md shadow-md py-2" 
          : "bg-transparent py-4"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#home" 
          className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors ${
            scrolled ? "text-alpine-green" : "text-white"
          }`}
          data-testid="logo-link"
        >
          Golf Hotel Des Alpes
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className={`nav-link font-body text-sm uppercase tracking-wider transition-colors ${
                scrolled ? "text-alpine-green hover:text-warm-wood" : "text-white hover:text-muted-gold"
              }`}
              data-testid={`nav-${item.key}`}
            >
              {t.nav[item.key]}
            </a>
          ))}
          
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className={`flex items-center gap-1 ${
                  scrolled ? "text-alpine-green hover:bg-alpine-green/10" : "text-white hover:bg-white/10"
                }`}
                data-testid="language-selector"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs">{lang}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              {Object.entries(languageNames).map(([code, name]) => (
                <DropdownMenuItem
                  key={code}
                  onClick={() => setLang(code)}
                  className={`cursor-pointer ${code === lang ? "bg-alpine-green/10 text-alpine-green" : ""}`}
                  data-testid={`lang-${code}`}
                >
                  {name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Book Now Button */}
          <Button
            asChild
            className="bg-muted-gold text-alpine-green hover:bg-alpine-green hover:text-white font-semibold"
            data-testid="book-now-btn"
          >
            <a href={getBookingUrl(lang)} target="_blank" rel="noopener noreferrer">
              {t.nav.book}
            </a>
          </Button>
        </nav>

        {/* Mobile Menu */}
        <div className="flex items-center gap-2 lg:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className={scrolled ? "text-alpine-green" : "text-white"}
              >
                <Globe className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              {Object.entries(languageNames).map(([code, name]) => (
                <DropdownMenuItem
                  key={code}
                  onClick={() => setLang(code)}
                  className={`cursor-pointer ${code === lang ? "bg-alpine-green/10" : ""}`}
                >
                  {name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className={scrolled ? "text-alpine-green" : "text-white"}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-snow-cream w-[280px]">
              <div className="flex flex-col gap-6 mt-8">
                {navItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-heading text-xl text-alpine-green hover:text-warm-wood transition-colors"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {t.nav[item.key]}
                  </a>
                ))}
                <Separator className="my-2" />
                <Button
                  asChild
                  className="bg-muted-gold text-alpine-green hover:bg-alpine-green hover:text-white w-full"
                >
                  <a href={getBookingUrl(lang)} target="_blank" rel="noopener noreferrer">
                    {t.nav.book}
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ t, lang }) => (
  <section 
    id="home" 
    className="relative h-screen w-full flex items-center justify-center overflow-hidden"
    data-testid="hero-section"
  >
    {/* Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${images.hero})` }}
    />
    
    {/* Overlay */}
    <div className="hero-gradient absolute inset-0" />
    
    {/* Content */}
    <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
      <h1 
        className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 animate-fade-in-up"
        data-testid="hero-title"
      >
        {t.hero.title}
      </h1>
      <p 
        className="font-body text-lg md:text-xl lg:text-2xl mb-8 opacity-90 animate-fade-in-up animate-delay-200"
        data-testid="hero-subtitle"
      >
        {t.hero.subtitle}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animate-delay-300">
        <Button
          asChild
          size="lg"
          className="bg-muted-gold text-alpine-green hover:bg-white text-lg px-8 py-6 font-semibold"
          data-testid="hero-book-btn"
        >
          <a href={getBookingUrl(lang)} target="_blank" rel="noopener noreferrer">
            {t.nav.book}
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          size="lg"
          className="border-white text-white bg-white/10 hover:bg-white hover:text-alpine-green text-lg px-8 py-6"
          data-testid="hero-discover-btn"
        >
          <a href="#rooms">
            {t.hero.cta}
          </a>
        </Button>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronDown className="w-8 h-8 text-white/80" />
    </div>
  </section>
);

// About Section
const AboutSection = ({ t }) => (
  <section 
    id="about" 
    className="py-16 md:py-24 bg-snow-cream"
    data-testid="about-section"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Image */}
        <div className="relative overflow-hidden rounded-lg shadow-xl">
          <img
            src={images.landscape}
            alt="Engadin Valley"
            className="w-full h-[300px] md:h-[400px] object-cover object-left-bottom img-zoom"
            loading="lazy"
          />
        </div>
        
        {/* Content */}
        <div>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-alpine-green mb-6"
            data-testid="about-title"
          >
            {t.about.title}
          </h2>
          <p className="font-body text-text-secondary text-lg leading-relaxed mb-8">
            {t.about.description}
          </p>
          
          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Bed, text: t.about.features.rooms },
              { icon: Utensils, text: t.about.features.restaurant },
              { icon: Wine, text: t.about.features.bar },
              { icon: Dog, text: t.about.features.petFriendly }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg bg-white shadow-sm"
              >
                <item.icon className="w-5 h-5 text-warm-wood" />
                <span className="font-body text-alpine-green font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Room Card Component
const RoomCard = ({ room, t, lang, featured = false }) => {
  const hasMultipleImages = Array.isArray(room.image);
  
  return (
    <Card 
      className={`room-card overflow-hidden bg-white ${featured ? "lg:col-span-2 lg:row-span-2" : ""}`}
      data-testid={`room-card-${room.key}`}
    >
      <div className={`relative overflow-hidden ${featured ? "h-64 lg:h-80" : "h-48 md:h-56"}`}>
        {hasMultipleImages ? (
          <Carousel className="w-full h-full" opts={{ loop: true }}>
            <CarouselContent className="h-full -ml-0">
              {room.image.map((img, index) => (
                <CarouselItem key={index} className="h-full pl-0">
                  <img
                    src={img}
                    alt={`${room.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 bg-white/80 hover:bg-white border-0" />
            <CarouselNext className="right-2 bg-white/80 hover:bg-white border-0" />
          </Carousel>
        ) : (
          <img
            src={room.image}
            alt={room.name}
            className="w-full h-full object-cover img-zoom"
            loading="lazy"
          />
        )}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full z-10">
          <span className="font-body text-sm text-alpine-green font-medium">{room.size}</span>
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="font-heading text-xl md:text-2xl text-alpine-green mb-2">
          {room.name}
        </h3>
        <p className="font-body text-text-secondary text-sm mb-4 line-clamp-3">
          {room.description}
        </p>
        
        {/* Amenities */}
        <div className="flex flex-wrap gap-2 mb-4">
          {room.amenities.map((amenity, index) => (
            <span 
              key={index}
              className="flex items-center gap-1 text-xs text-warm-wood bg-warm-wood/10 px-2 py-1 rounded-full"
            >
              <Check className="w-3 h-3" />
              {amenity}
            </span>
          ))}
        </div>
        
        <Button
          asChild
          className="w-full bg-alpine-green hover:bg-muted-gold hover:text-alpine-green transition-all"
          data-testid={`book-${room.key}-btn`}
        >
          <a href={getBookingUrl(lang)} target="_blank" rel="noopener noreferrer">
            {t.nav.book}
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};

// Rooms Section
const RoomsSection = ({ t, lang }) => {
  const rooms = [
    {
      key: "standard",
      image: images.rooms.standard,
      name: t.rooms.standard.name,
      size: t.rooms.standard.size,
      description: t.rooms.standard.description,
      amenities: [t.rooms.amenities.doubleBed, t.rooms.amenities.bathroom, t.rooms.amenities.tv, t.rooms.amenities.desk]
    },
    {
      key: "superior",
      image: images.rooms.superior,
      name: t.rooms.superior.name,
      size: t.rooms.superior.size,
      description: t.rooms.superior.description,
      amenities: [t.rooms.amenities.doubleBed, t.rooms.amenities.bathroom, t.rooms.amenities.tv, t.rooms.amenities.desk]
    },
    {
      key: "mountainView",
      image: images.rooms.mountainView,
      name: t.rooms.mountainView.name,
      size: t.rooms.mountainView.size,
      description: t.rooms.mountainView.description,
      amenities: [t.rooms.amenities.doubleBed, t.rooms.amenities.mountainView, t.rooms.amenities.bathroom, t.rooms.amenities.tv]
    },
    {
      key: "balcony",
      image: images.rooms.balcony,
      name: t.rooms.balcony.name,
      size: t.rooms.balcony.size,
      description: t.rooms.balcony.description,
      amenities: [t.rooms.amenities.doubleBed, t.rooms.amenities.balcony, t.rooms.amenities.mountainView, t.rooms.amenities.bathroom],
      featured: true
    },
    {
      key: "family",
      image: images.rooms.family,
      name: t.rooms.family.name,
      size: t.rooms.family.size,
      description: t.rooms.family.description,
      amenities: [t.rooms.amenities.doubleBed, t.rooms.amenities.bunkBed, t.rooms.amenities.bathroom, t.rooms.amenities.tv]
    }
  ];

  return (
    <section 
      id="rooms" 
      className="py-16 md:py-24 bg-white"
      data-testid="rooms-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-alpine-green mb-4"
            data-testid="rooms-title"
          >
            {t.rooms.title}
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-2xl mx-auto">
            {t.rooms.subtitle}
          </p>
        </div>

        {/* Rooms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {rooms.map((room) => (
            <RoomCard 
              key={room.key} 
              room={room} 
              t={t} 
              lang={lang}
              featured={room.featured}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ t }) => {
  const services = [
    { icon: Utensils, ...t.services.breakfast, key: "breakfast", image: images.dining.breakfast },
    { icon: Wine, ...t.services.restaurant, key: "restaurant", image: images.dining.restaurant },
    { icon: Wine, ...t.services.bar, key: "bar" },
    { icon: Dog, ...t.services.pets, key: "pets" },
    { icon: Snowflake, ...t.services.ski, key: "ski" },
    { icon: Star, ...t.services.golf, key: "golf", image: images.golf }
  ];

  return (
    <section 
      id="services" 
      className="py-16 md:py-24 bg-snow-cream"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-alpine-green mb-4"
            data-testid="services-title"
          >
            {t.services.title}
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service) => (
            <div 
              key={service.key}
              className="service-card bg-white rounded-lg p-6 text-center"
              data-testid={`service-${service.key}`}
            >
              {service.image ? (
                <div className="relative w-full h-40 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-alpine-green/10 flex items-center justify-center">
                  <service.icon className="w-8 h-8 text-alpine-green" />
                </div>
              )}
              <h3 className="font-heading text-xl text-alpine-green mb-2">
                {service.title}
              </h3>
              <p className="font-body text-text-secondary text-sm">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = ({ t }) => {
  const locationFeatures = [
    { icon: TrainFront, text: t.location.train },
    { icon: Bus, text: t.location.bus },
    { icon: Star, text: t.location.golf },
    { icon: Snowflake, text: t.location.ski }
  ];

  return (
    <section 
      id="location" 
      className="py-16 md:py-24 bg-white"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-alpine-green mb-4"
            data-testid="location-title"
          >
            {t.location.title}
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-2xl mx-auto">
            {t.location.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Map placeholder / Image */}
          <div className="relative overflow-hidden rounded-lg shadow-xl h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2736.852661073!2d9.869749!3d46.533889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4784060a9a9a9a9b%3A0x0!2sVia%20San%20Bastiaun%2025%2C%207503%20Samedan%2C%20Switzerland!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Golf Hotel Des Alpes Location"
              className="rounded-lg"
            />
          </div>

          {/* Location Info */}
          <div>
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-alpine-green/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-alpine-green" />
              </div>
              <div>
                <h3 className="font-heading text-xl text-alpine-green mb-2">
                  {t.contact.address}
                </h3>
                <p className="font-body text-text-secondary">
                  {t.location.address}
                </p>
              </div>
            </div>

            {/* Distance Features */}
            <div className="grid grid-cols-2 gap-4">
              {locationFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-lg bg-snow-cream"
                >
                  <feature.icon className="w-5 h-5 text-warm-wood" />
                  <span className="font-body text-alpine-green text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ t, lang }) => (
  <section 
    id="contact" 
    className="py-16 md:py-24 bg-alpine-green text-white"
    data-testid="contact-section"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      {/* Section Header */}
      <div className="text-center mb-12 md:mb-16">
        <h2 
          className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4"
          data-testid="contact-title"
        >
          {t.contact.title}
        </h2>
        <p className="font-body text-snow-cream/80 text-lg max-w-2xl mx-auto">
          {t.contact.subtitle}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {/* Phone/WhatsApp */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
            <Phone className="w-8 h-8" />
          </div>
          <h3 className="font-heading text-xl mb-2">{t.contact.phone}</h3>
          <a 
            href="https://wa.me/41782620740"
            target="_blank"
            rel="noopener noreferrer"
            className="font-body text-snow-cream/90 hover:text-muted-gold transition-colors"
            data-testid="whatsapp-link"
          >
            +41 78 262 07 40
          </a>
        </div>

        {/* Email */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
            <Mail className="w-8 h-8" />
          </div>
          <h3 className="font-heading text-xl mb-2">{t.contact.email}</h3>
          <ObfuscatedEmail />
        </div>

        {/* Address */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
            <MapPin className="w-8 h-8" />
          </div>
          <h3 className="font-heading text-xl mb-2">{t.contact.address}</h3>
          <p className="font-body text-snow-cream/90">
            Via San Bastiaun 25<br />
            7503 Samedan, CH
          </p>
        </div>
      </div>

      {/* Book Now CTA */}
      <div className="text-center">
        <Button
          asChild
          size="lg"
          className="bg-muted-gold text-alpine-green hover:bg-white text-lg px-12 py-6 font-semibold"
          data-testid="contact-book-btn"
        >
          <a href={getBookingUrl(lang)} target="_blank" rel="noopener noreferrer">
            {t.nav.book}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

// Cookie Consent Banner Component
const CookieConsent = ({ t, onAcceptAll, onRejectAll, onOpenSettings }) => (
  <div 
    className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-gray-200 p-4 md:p-6"
    data-testid="cookie-banner"
  >
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Cookie className="w-6 h-6 text-warm-wood flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-heading text-lg text-alpine-green mb-1">{t.gdpr.title}</h3>
            <p className="font-body text-sm text-text-secondary">{t.gdpr.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenSettings}
            className="flex-1 md:flex-none border-alpine-green text-alpine-green hover:bg-alpine-green hover:text-white"
            data-testid="cookie-settings-btn"
          >
            {t.gdpr.settings}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRejectAll}
            className="flex-1 md:flex-none"
            data-testid="cookie-reject-btn"
          >
            {t.gdpr.rejectAll}
          </Button>
          <Button
            size="sm"
            onClick={onAcceptAll}
            className="flex-1 md:flex-none bg-alpine-green hover:bg-muted-gold hover:text-alpine-green"
            data-testid="cookie-accept-btn"
          >
            {t.gdpr.acceptAll}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// Cookie Settings Modal
const CookieSettingsModal = ({ t, isOpen, onClose, cookiePrefs, setCookiePrefs, onSave }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-lg bg-white">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl text-alpine-green flex items-center gap-2">
          <Cookie className="w-6 h-6" />
          {t.gdpr.title}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        {/* Necessary Cookies */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-body font-semibold text-alpine-green">{t.gdpr.necessary}</h4>
            <p className="font-body text-sm text-text-secondary">{t.gdpr.necessaryDesc}</p>
          </div>
          <Switch checked={true} disabled className="data-[state=checked]:bg-alpine-green" />
        </div>
        
        <Separator />
        
        {/* Analytics Cookies */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-body font-semibold text-alpine-green">{t.gdpr.analytics}</h4>
            <p className="font-body text-sm text-text-secondary">{t.gdpr.analyticsDesc}</p>
          </div>
          <Switch 
            checked={cookiePrefs.analytics} 
            onCheckedChange={(checked) => setCookiePrefs(prev => ({ ...prev, analytics: checked }))}
            className="data-[state=checked]:bg-alpine-green"
            data-testid="analytics-toggle"
          />
        </div>
        
        <Separator />
        
        {/* Marketing Cookies */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="font-body font-semibold text-alpine-green">{t.gdpr.marketing}</h4>
            <p className="font-body text-sm text-text-secondary">{t.gdpr.marketingDesc}</p>
          </div>
          <Switch 
            checked={cookiePrefs.marketing} 
            onCheckedChange={(checked) => setCookiePrefs(prev => ({ ...prev, marketing: checked }))}
            className="data-[state=checked]:bg-alpine-green"
            data-testid="marketing-toggle"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose}>
          {t.gdpr.rejectAll}
        </Button>
        <Button 
          onClick={onSave}
          className="bg-alpine-green hover:bg-muted-gold hover:text-alpine-green"
          data-testid="save-cookie-prefs-btn"
        >
          {t.gdpr.acceptSelected}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Privacy Policy Modal
const PrivacyPolicyModal = ({ t, isOpen, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl text-alpine-green flex items-center gap-2">
          <Shield className="w-6 h-6" />
          {t.gdpr.privacyTitle}
        </DialogTitle>
      </DialogHeader>
      <div 
        className="prose prose-sm max-w-none py-4 font-body text-text-secondary
          [&_h3]:font-heading [&_h3]:text-alpine-green [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2
          [&_p]:mb-4 [&_p]:leading-relaxed"
        dangerouslySetInnerHTML={{ __html: t.gdpr.privacyContent }}
      />
      <div className="flex justify-end pt-4">
        <Button onClick={onClose} className="bg-alpine-green hover:bg-muted-gold hover:text-alpine-green">
          OK
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Footer Component
const Footer = ({ t, onOpenPrivacy, onOpenCookieSettings }) => (
  <footer 
    className="bg-alpine-green text-white py-12 border-t border-white/10"
    data-testid="footer"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid md:grid-cols-3 gap-8 mb-8">
        {/* Hotel Info */}
        <div>
          <h3 className="font-heading text-xl mb-4">Golf Hotel Des Alpes</h3>
          <p className="font-body text-snow-cream/70 text-sm mb-2">
            Via San Bastiaun 25
          </p>
          <p className="font-body text-snow-cream/70 text-sm mb-4">
            7503 Samedan, CH
          </p>
          <p className="font-body text-snow-cream/70 text-sm">
            T. +41 78 262 07 40
          </p>
        </div>

        {/* Company Info */}
        <div>
          <h3 className="font-heading text-xl mb-4">{t.contact.company}</h3>
          <p className="font-body text-snow-cream/70 text-sm mb-2">
            {t.contact.companyAddress}
          </p>
          <p className="font-body text-snow-cream/70 text-sm">
            {t.contact.vatNumber}
          </p>
        </div>

        {/* Social Links */}
        <div>
          <h3 className="font-heading text-xl mb-4">{t.footer.followUs}</h3>
          <div className="flex gap-4">
            <a
              href="https://www.facebook.com/people/Golf-Hotel-Des-Alpes/61567719388801/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-muted-gold hover:text-alpine-green transition-all"
              aria-label="Facebook"
              data-testid="facebook-link"
            >
              <FacebookIcon />
            </a>
            <a
              href="https://www.instagram.com/golfhoteldesalpes/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-muted-gold hover:text-alpine-green transition-all"
              aria-label="Instagram"
              data-testid="instagram-link"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://wa.me/41782620740"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon whatsapp-pulse w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center hover:bg-muted-gold transition-all"
              aria-label="WhatsApp"
              data-testid="footer-whatsapp-link"
            >
              <WhatsAppIcon />
            </a>
          </div>
        </div>
      </div>

      <Separator className="bg-white/20 mb-8" />

      {/* Privacy & Cookie Links */}
      <div className="flex flex-wrap justify-center gap-4 mb-6">
        <button
          onClick={onOpenPrivacy}
          className="font-body text-snow-cream/60 text-sm hover:text-muted-gold transition-colors underline-offset-2 hover:underline"
          data-testid="privacy-link"
        >
          {t.gdpr.privacyPolicy}
        </button>
        <span className="text-snow-cream/40">|</span>
        <button
          onClick={onOpenCookieSettings}
          className="font-body text-snow-cream/60 text-sm hover:text-muted-gold transition-colors underline-offset-2 hover:underline"
          data-testid="cookie-settings-link"
        >
          {t.gdpr.settings}
        </button>
      </div>

      {/* Copyright */}
      <div className="text-center">
        <p className="font-body text-snow-cream/60 text-sm mb-4">
          © {new Date().getFullYear()} Golf Hotel Des Alpes. {t.footer.rights}
        </p>
        <a 
          href="https://telcavoip.ai/it/services/webdesign" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-snow-cream/40 hover:text-muted-gold text-xs transition-colors"
          data-testid="telcavoip-badge"
        >
          <span>Made With</span>
          <svg className="w-3 h-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
          </svg>
          <span>By TelcaVoIP International</span>
        </a>
      </div>
    </div>
  </footer>
);

// AI Chat Popup Component
const ChatPopup = ({ t, lang, onClose }) => (
  <div 
    className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
    data-testid="chat-popup"
  >
    <div className="bg-alpine-green p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-heading text-lg text-white">{t.chatPopup.title}</h3>
        </div>
        <button 
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
    <div className="p-4">
      <p className="font-body text-sm text-text-secondary mb-4">
        {t.chatPopup.description}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="flex-1"
        >
          {t.chatPopup.close}
        </Button>
        <Button
          asChild
          size="sm"
          className="flex-1 bg-alpine-green hover:bg-muted-gold hover:text-alpine-green"
        >
          <a href={getChatbotUrl(lang)} target="_blank" rel="noopener noreferrer">
            {t.chatPopup.button}
          </a>
        </Button>
      </div>
    </div>
  </div>
);

// Floating WhatsApp Button
const FloatingWhatsApp = ({ hasConsent }) => (
  <a
    href="https://wa.me/41782620740"
    target="_blank"
    rel="noopener noreferrer"
    className={`fixed ${hasConsent ? 'bottom-6' : 'bottom-28'} right-6 z-40 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg whatsapp-pulse hover:scale-110 transition-all`}
    aria-label="Contact via WhatsApp"
    data-testid="floating-whatsapp"
  >
    <WhatsAppIcon />
  </a>
);

// Main App Component
function App() {
  const [lang, setLang] = useState(() => detectLanguage());
  const [scrolled, setScrolled] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const t = translations[lang];

  // Check cookie consent on mount
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieBanner(true);
    } else {
      setCookiePrefs(JSON.parse(consent));
    }
  }, []);

  // Handle scroll for navigation styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load Robottino chat widget based on language - reload page on language change
  useEffect(() => {
    // Store language in localStorage
    localStorage.setItem('selectedLanguage', lang);
  }, [lang]);

  // Show AI popup after 5 seconds (only once per session)
  useEffect(() => {
    const popupShown = sessionStorage.getItem('aiPopupShown');
    if (!popupShown) {
      const timer = setTimeout(() => {
        setShowChatPopup(true);
        sessionStorage.setItem('aiPopupShown', 'true');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cookie consent handlers
  const handleAcceptAll = () => {
    const prefs = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setCookiePrefs(prefs);
    setShowCookieBanner(false);
  };

  const handleRejectAll = () => {
    const prefs = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setCookiePrefs(prefs);
    setShowCookieBanner(false);
  };

  const handleSavePrefs = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePrefs));
    setShowCookieBanner(false);
    setShowCookieSettings(false);
  };

  return (
    <div className="App min-h-screen bg-snow-cream" data-testid="app-container">
      <Navigation t={t} lang={lang} setLang={setLang} scrolled={scrolled} />
      <main>
        <HeroSection t={t} lang={lang} />
        <AboutSection t={t} />
        <RoomsSection t={t} lang={lang} />
        <ServicesSection t={t} />
        <LocationSection t={t} />
        <ContactSection t={t} lang={lang} />
      </main>
      <Footer 
        t={t} 
        onOpenPrivacy={() => setShowPrivacyPolicy(true)}
        onOpenCookieSettings={() => setShowCookieSettings(true)}
      />
      <FloatingWhatsApp hasConsent={!showCookieBanner} />
      
      {/* AI Chat Popup */}
      {showChatPopup && (
        <ChatPopup 
          t={t}
          lang={lang}
          onClose={() => setShowChatPopup(false)}
        />
      )}
      
      {/* GDPR Cookie Banner */}
      {showCookieBanner && (
        <CookieConsent 
          t={t}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onOpenSettings={() => setShowCookieSettings(true)}
        />
      )}
      
      {/* Cookie Settings Modal */}
      <CookieSettingsModal
        t={t}
        isOpen={showCookieSettings}
        onClose={() => setShowCookieSettings(false)}
        cookiePrefs={cookiePrefs}
        setCookiePrefs={setCookiePrefs}
        onSave={handleSavePrefs}
      />
      
      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        t={t}
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
    </div>
  );
}

export default App;
